import { createResource, createSignal, Index, onCleanup, onMount, Show } from 'solid-js';
import { A, useNavigate, useSearchParams } from '@solidjs/router';
import { useApi, isOk, type SeedCrackWorldDetailDto } from '../api';
import { useTranslation } from '../hooks/useTranslation';
import Text from '../components/Text';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import { useUserContext } from '../components/UserContextProvider';
import { useRoleParser } from '../hooks/permissions';
import { formatDateTime } from '../services/dateFormat';
import {
    SEED_CRACK_POLL_MS,
    SEED_CRACK_STATUS_CRACKED,
    SEED_CRACK_STATUS_CRACKING,
    SEED_CRACK_STATUS_AMBIGUOUS,
    seedCrackStatusLabelKey,
    seedCrackStatusIcon,
    dimensionLabel,
    formatCandidates,
    formatEta,
} from '../services/seedCrack';
import styles from './SeedCrackerDetail.module.css';

function statusClass(status: number): string {
    switch (status) {
        case 1: return styles.statusFeasible;
        case 2: return styles.statusCracking;
        case 3: return styles.statusCracked;
        case 4: return styles.statusAmbiguous;
        default: return styles.statusCollecting;
    }
}

export default function SeedCrackerDetail() {
    const api = useApi();
    const { t } = useTranslation();
    const toast = useToast();
    const navigate = useNavigate();
    const { user } = useUserContext();
    const { parseRoles, roles } = useRoleParser();
    const [searchParams] = useSearchParams();

    const worldKey = () => (searchParams.key as string | undefined) ?? '';

    const isPlatformAdmin = () => {
        const flags = parseRoles(user()?.permissions ?? 0, roles);
        return (!!flags[roles.indexOf('Admin')] || !!flags[roles.indexOf('Root')]) && user()?.showAdminUiInMemberApp === true;
    };

    const [deepBusy, setDeepBusy] = createSignal(false);
    const [clearBusy, setClearBusy] = createSignal(false);
    // Which row is mid-delete: the chunk key, or "unread" for the bulk drop. One at a time, so a double click cannot fire two.
    const [fortressBusy, setFortressBusy] = createSignal<string | null>(null);

    async function clearWorldData() {
        const key = worldKey();
        if (!key || clearBusy()) return;
        if (!(await showConfirm(t('SEED_CRACK_CLEAR_CONFIRM'), true))) return;
        setClearBusy(true);
        try {
            const res = await api.seedCrack.clearWorld({ key });
            if (isOk(res.data)) {
                toast?.success(t('SEED_CRACK_CLEARED'));
                navigate('/seed-cracker');
            } else {
                toast?.error(t('SEED_CRACK_CLEAR_FAILED'));
            }
        } catch {
            toast?.error(t('SEED_CRACK_CLEAR_FAILED'));
        } finally {
            setClearBusy(false);
        }
    }

    // Dropping a fortress is a real tool, not housekeeping: every stored start chunk filters the sweep, so one that is not
    // really a start excludes the true seed and the sweep runs to the end and finds nothing.
    async function deleteFortress(chunkX: number, chunkZ: number) {
        const key = worldKey();
        if (!key || fortressBusy() !== null) return;
        if (!(await showConfirm(t('SEED_CRACK_DIAG_DELETE_CONFIRM', chunkX, chunkZ), true))) return;
        setFortressBusy(`${chunkX}:${chunkZ}`);
        try {
            const res = await api.seedCrack.deleteFortress({ key, chunkX, chunkZ });
            if (isOk(res.data)) {
                toast?.success(t('SEED_CRACK_DIAG_DELETED'));
                void refetch();
            } else {
                toast?.error(t('SEED_CRACK_DIAG_DELETE_FAILED'));
            }
        } catch {
            toast?.error(t('SEED_CRACK_DIAG_DELETE_FAILED'));
        } finally {
            setFortressBusy(null);
        }
    }

    async function deleteFortressesWithoutLayout() {
        const key = worldKey();
        if (!key || fortressBusy() !== null) return;
        if (!(await showConfirm(t('SEED_CRACK_DIAG_DELETE_UNREAD_CONFIRM'), true))) return;
        setFortressBusy('unread');
        try {
            const res = await api.seedCrack.deleteFortress({ key, onlyWithoutLayout: true });
            if (isOk(res.data)) {
                toast?.success(t('SEED_CRACK_DIAG_DELETED'));
                void refetch();
            } else {
                toast?.error(t('SEED_CRACK_DIAG_DELETE_FAILED'));
            }
        } catch {
            toast?.error(t('SEED_CRACK_DIAG_DELETE_FAILED'));
        } finally {
            setFortressBusy(null);
        }
    }

    async function runDeepCrack() {
        const key = worldKey();
        if (!key || deepBusy()) return;
        setDeepBusy(true);
        try {
            const res = await api.seedCrack.deepCrack({ key });
            if (isOk(res.data)) {
                toast?.info(t('SEED_CRACK_DEEP_STARTED'));
                void refetch();
            } else {
                toast?.error(t('SEED_CRACK_DEEP_FAILED'));
            }
        } catch {
            toast?.error(t('SEED_CRACK_DEEP_FAILED'));
        } finally {
            setDeepBusy(false);
        }
    }

    // Keyed on worldKey so navigating straight from one world's detail to another refetches instead of
    // showing the previous world. refetch() drives the silent 5s poll without resetting to the spinner.
    const [world, { refetch }] = createResource<SeedCrackWorldDetailDto | null, string>(
        worldKey,
        async (key) => {
            if (!key) return null;
            const res = await api.seedCrack.getWorld({ key });
            return isOk(res.data) && res.data.value ? res.data.value : null;
        },
    );

    onMount(() => {
        const handle = window.setInterval(() => {
            if (document.visibilityState === 'visible') void refetch();
        }, SEED_CRACK_POLL_MS);
        onCleanup(() => clearInterval(handle));
    });

    return (
        <div class={styles.container}>
            <A href="/seed-cracker" class={styles.backLink}>
                <i class="fa-solid fa-arrow-left" /><Text val="SEED_CRACK_BACK" />
            </A>

            <Show when={world() !== undefined} fallback={
                <div class={styles.spinnerWrap}>
                    <i class="fa-solid fa-spinner fa-spin fa-2x" />
                </div>
            }>
                <Show when={world()} fallback={
                    <div class={styles.empty}>
                        <i class="fa-solid fa-circle-question" />
                        <p><Text val="SEED_CRACK_DETAIL_NOT_FOUND" /></p>
                    </div>
                }>
                    {(w) => (
                        <>
                            <div class={styles.header}>
                                <div class={styles.headerTitle}>
                                    <i class="fa-solid fa-seedling" />
                                    <h1>{w().citybuild || t('SEED_CRACK_CB_UNKNOWN')}</h1>
                                    <span class={styles.dimensionBadge}>
                                        <i class="fa-solid fa-earth-europe" />{dimensionLabel(t, w().dimension)}
                                    </span>
                                    <Show when={w().isGlitch}>
                                        <span class={styles.glitchBadge}>
                                            <i class="fa-solid fa-bug" /><Text val="SEED_CRACK_GLITCH" />
                                        </span>
                                    </Show>
                                </div>
                                <div class={styles.headerMeta}>
                                    <span class={styles.serverAddress}><i class="fa-solid fa-network-wired" /> {w().serverAddress}</span>
                                    <span><Text val="SEED_CRACK_CENTER" /> <code>{w().centerX} / {w().centerZ}</code></span>
                                    <span><Text val="SEED_CRACK_DIAMETER" /> <code>{w().diameter}</code></span>
                                </div>
                            </div>

                            <div class={`fcard ${styles.crackPanel}`}>
                                <div class={styles.crackPanelTop}>
                                    <span class={styles.crackPanelLabel}><Text val="SEED_CRACK_RESULT" /></span>
                                    <span class={`${styles.statusBadge} ${statusClass(w().status)}`}>
                                        <i class={w().status === SEED_CRACK_STATUS_CRACKING ? 'fa-solid fa-spinner fa-spin' : seedCrackStatusIcon(w().status)} />
                                        <Text val={seedCrackStatusLabelKey(w().status)} />
                                    </span>
                                </div>

                                <Show when={w().status === SEED_CRACK_STATUS_CRACKED && w().crackedSeedText != null} fallback={
                                    <div class={styles.pending}>
                                        <div class={styles.pendingStat}>
                                            <span class={styles.pendingLabel}><Text val="SEED_CRACK_DUNGEONS_USABLE" /></span>
                                            <span class={styles.candidatesValue}>{w().usableDungeonCount.toLocaleString()} / {w().dungeonCount.toLocaleString()}</span>
                                        </div>
                                        <div class={styles.pendingStat}>
                                            <span class={styles.pendingLabel}><Text val="SEED_CRACK_CANDIDATES" /></span>
                                            <span class={styles.candidatesValue}>{formatCandidates(w().estimatedCandidates)}</span>
                                        </div>
                                        <Show when={w().status !== SEED_CRACK_STATUS_CRACKING}>
                                            <div class={styles.pendingStat}>
                                                <span class={styles.pendingLabel}><Text val="SEED_CRACK_ETA" /></span>
                                                <span class={styles.etaValue}>{formatEta(t, w().etaSeconds)}</span>
                                            </div>
                                        </Show>
                                    </div>
                                }>
                                    <div class={styles.crackedResult}>
                                        <span class={styles.seedLabel}><Text val="SEED_CRACK_SEED" /></span>
                                        <span class={styles.seedValue}>{w().crackedSeedText}</span>
                                    </div>
                                </Show>

                                <p class={styles.explainer}>
                                    <i class="fa-solid fa-circle-info" />
                                    <span>{w().statusMessage || t('SEED_CRACK_EXPLAINER')}</span>
                                </p>
                            </div>

                            {/* Only present when SEED_CRACKER_DIAGNOSTICS is on for this user. The summary above can say
                                what is missing; a Nether crack needs to know which fortress is holding it up. */}
                            <Show when={w().diagnostics}>
                                {(diag) => (
                                    <div class={`fcard ${styles.diagnostics}`}>
                                        <span class={styles.diagnosticsHead}>
                                            <i class="fa-solid fa-stethoscope" />
                                            <Text val="SEED_CRACK_DIAG_TITLE" />
                                        </span>

                                        <p class={styles.explainer}>
                                            <i class="fa-solid fa-lightbulb" />
                                            <span>{diag().explanation}</span>
                                        </p>

                                        <div class="row" style={{ gap: 'var(--spacing-lg)', 'flex-wrap': 'wrap' }}>
                                            <div class="v">
                                                <span class={styles.statLabel}><Text val="SEED_CRACK_DIAG_WITH_LAYOUT" /></span>
                                                <code>{diag().fortressesWithLayout} / {diag().fortresses.length}</code>
                                            </div>
                                            <div class="v">
                                                <span class={styles.statLabel}><Text val="SEED_CRACK_DIAG_SWEEP" /></span>
                                                <code>{diag().sweepPercent.toFixed(2)}%</code>
                                            </div>
                                            <div class="v">
                                                <span class={styles.statLabel}><Text val="SEED_CRACK_DIAG_SWEEP_CANDIDATES" /></span>
                                                <code>{diag().sweepCandidates}</code>
                                            </div>
                                            <Show when={diag().sweptLayoutSize}>
                                                <div class="v">
                                                    <span class={styles.statLabel}><Text val="SEED_CRACK_DIAG_LAYOUT_BYTES" /></span>
                                                    <code>{diag().sweptLayoutSize}</code>
                                                </div>
                                            </Show>
                                        </div>

                                        <div class={styles.sweepBar}>
                                            <div class={styles.sweepBarFill} style={{ width: `${Math.min(100, diag().sweepPercent)}%` }} />
                                        </div>

                                        <Show
                                            when={diag().fortresses.length > 0}
                                            fallback={<span class={styles.statLabel}><Text val="SEED_CRACK_DIAG_NO_FORTRESSES" /></span>}
                                        >
                                            <div class={styles.fortressScroll}>
                                                <table class={styles.fortressTable}>
                                                    <thead>
                                                        <tr>
                                                            <th><Text val="SEED_CRACK_DIAG_CHUNK" /></th>
                                                            <th><Text val="SEED_CRACK_DIAG_REGION" /></th>
                                                            <th><Text val="SEED_CRACK_DIAG_LAYOUT" /></th>
                                                            <th><Text val="SEED_CRACK_DIAG_PIECES" /></th>
                                                            <th><Text val="SEED_CRACK_DIAG_CERTAIN" /></th>
                                                            <th><Text val="SEED_CRACK_DIAG_LAST_READ" /></th>
                                                            <Show when={isPlatformAdmin()}>
                                                                <th class={styles.fortressActions} />
                                                            </Show>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <Index each={diag().fortresses}>
                                                            {(fortress) => (
                                                                <tr>
                                                                    <td>{fortress().chunkX} / {fortress().chunkZ}</td>
                                                                    <td>{fortress().regionX} / {fortress().regionZ}</td>
                                                                    <td>
                                                                        <Show
                                                                            when={fortress().hasLayout}
                                                                            fallback={<i class="fa-solid fa-xmark" style={{ color: 'var(--dark-text-muted)' }} />}
                                                                        >
                                                                            <i class="fa-solid fa-check" style={{ color: 'var(--primary-400)' }} />
                                                                        </Show>
                                                                    </td>
                                                                    <td>{fortress().layoutPieces}</td>
                                                                    <td>{fortress().layoutCertainPieces}</td>
                                                                    <td class={styles.readNote}>
                                                                        {fortress().readNote || t('SEED_CRACK_DIAG_NEVER_READ')}
                                                                    </td>
                                                                    <Show when={isPlatformAdmin()}>
                                                                        <td class={styles.fortressActions}>
                                                                            <button
                                                                                class="btn btn-danger btn-icon btn-sm"
                                                                                title={t('SEED_CRACK_DIAG_DELETE')}
                                                                                disabled={fortressBusy() !== null}
                                                                                onClick={() => void deleteFortress(fortress().chunkX, fortress().chunkZ)}
                                                                            >
                                                                                <i
                                                                                    class={fortressBusy() === `${fortress().chunkX}:${fortress().chunkZ}`
                                                                                        ? 'fa-solid fa-spinner fa-spin'
                                                                                        : 'fa-solid fa-trash'}
                                                                                />
                                                                            </button>
                                                                        </td>
                                                                    </Show>
                                                                </tr>
                                                            )}
                                                        </Index>
                                                    </tbody>
                                                </table>
                                            </div>
                                            <Show when={isPlatformAdmin() && diag().fortresses.some((f) => !f.hasLayout)}>
                                                <div class={styles.fortressBulk}>
                                                    <button
                                                        class="btn btn-danger btn-sm"
                                                        disabled={fortressBusy() !== null}
                                                        onClick={() => void deleteFortressesWithoutLayout()}
                                                    >
                                                        <i
                                                            class={fortressBusy() === 'unread'
                                                                ? 'fa-solid fa-spinner fa-spin'
                                                                : 'fa-solid fa-broom'}
                                                        />
                                                        <Text val="SEED_CRACK_DIAG_DELETE_UNREAD" />
                                                    </button>
                                                    <span class={styles.fortressBulkHint}>
                                                        <Text val="SEED_CRACK_DIAG_DELETE_UNREAD_HINT" />
                                                    </span>
                                                </div>
                                            </Show>
                                        </Show>
                                    </div>
                                )}
                            </Show>

                            <Show when={w().status === SEED_CRACK_STATUS_AMBIGUOUS}>
                                <div class="v" style={{ gap: 'var(--spacing-sm)' }}>
                                    <button
                                        type="button"
                                        class="btn btn-secondary"
                                        style={{ width: 'fit-content', display: 'inline-flex', 'align-items': 'center', gap: 'var(--spacing-sm)' }}
                                        disabled={deepBusy()}
                                        onClick={runDeepCrack}
                                    >
                                        <i class={deepBusy() ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-layer-group'} />
                                        <Text val="SEED_CRACK_DEEP_CRACK" />
                                    </button>
                                    <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                                        <Text val="SEED_CRACK_DEEP_HINT" />
                                    </span>
                                </div>
                            </Show>

                            <Show when={w().status === SEED_CRACK_STATUS_CRACKED}>
                                <A href={`/seed-cracker/map?key=${encodeURIComponent(worldKey())}`} class="btn btn-primary" style={{ width: 'fit-content', display: 'inline-flex', 'align-items': 'center', gap: 'var(--spacing-sm)' }}>
                                    <i class="fa-solid fa-map" /><Text val="SEED_CRACK_MAP_VIEW" />
                                </A>
                            </Show>

                            <Show when={isPlatformAdmin()}>
                                <button
                                    type="button"
                                    class="btn btn-danger"
                                    style={{ width: 'fit-content', display: 'inline-flex', 'align-items': 'center', gap: 'var(--spacing-sm)' }}
                                    disabled={clearBusy()}
                                    onClick={clearWorldData}
                                >
                                    <i class={clearBusy() ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-trash'} />
                                    <Text val="SEED_CRACK_CLEAR_DATA" />
                                </button>
                            </Show>

                            <div class={styles.statsGrid}>
                                <div class={`fcard ${styles.statCard}`}>
                                    <span class={styles.statLabel}><Text val="SEED_CRACK_DUNGEONS" /></span>
                                    <span class={styles.statValue}>{w().dungeonCount.toLocaleString()}</span>
                                </div>
                                <div class={`fcard ${styles.statCard}`}>
                                    <span class={styles.statLabel}><Text val="SEED_CRACK_BIOMES" /></span>
                                    <span class={styles.statValue}>{w().biomeCount.toLocaleString()}</span>
                                </div>
                                <div class={`fcard ${styles.statCard}`}>
                                    <span class={styles.statLabel}><Text val="SEED_CRACK_STRUCTURES" /></span>
                                    <span class={styles.statValue}>{w().structureCount.toLocaleString()}</span>
                                </div>
                                <div class={`fcard ${styles.statCard}`}>
                                    <span class={styles.statLabel}><Text val="SEED_CRACK_FORTRESSES" /></span>
                                    <span class={styles.statValue}>{w().fortressCount.toLocaleString()}</span>
                                </div>
                                <div class={`fcard ${styles.statCard}`}>
                                    <span class={styles.statLabel}><Text val="SEED_CRACK_CONTRIBUTORS" /></span>
                                    <span class={styles.statValue}>{w().contributorCount.toLocaleString()}</span>
                                </div>
                            </div>

                            <div class={`fcard ${styles.timeline}`}>
                                <div class={styles.timelineRow}>
                                    <span class={styles.timelineLabel}><Text val="SEED_CRACK_FIRST_SEEN" /></span>
                                    <span class={styles.timelineValue}>{formatDateTime(w().firstSeenAt)}</span>
                                </div>
                                <div class={styles.timelineRow}>
                                    <span class={styles.timelineLabel}><Text val="SEED_CRACK_LAST_SEEN" /></span>
                                    <span class={styles.timelineValue}>{formatDateTime(w().lastSeenAt)}</span>
                                </div>
                                <div class={styles.timelineRow}>
                                    <span class={styles.timelineLabel}><Text val="SEED_CRACK_UPDATED" /></span>
                                    <span class={styles.timelineValue}>{formatDateTime(w().updatedAt)}</span>
                                </div>
                            </div>
                        </>
                    )}
                </Show>
            </Show>
        </div>
    );
}
